import Feature, { FeatureLike } from "ol/Feature";
import { Point } from "ol/geom";
import { Circle as CircleStyle, Fill, Icon, RegularShape, Stroke, Style, Text } from "ol/style";
import { IClusterConfig, IVectorLayerStyles } from "../config";
import { replaceFieldsWithCalculations, replaceFieldsWithValues } from "../utils";

export const getClusterSymbol = (isHovered: boolean, count: number, clusterConfig?: IClusterConfig): Style => {
    const style = clusterConfig.clusterStyle;
    const enlargeFactor = Number.isFinite(clusterConfig.enlargeOnHoverFactor) ? clusterConfig.enlargeOnHoverFactor : 2;
    const radius = (style?.radius || 14) - 1 + Math.log2(count) + (isHovered ? enlargeFactor : 0);
    return new Style({
        // image: new Icon({
        //     src: createImage(radius, count, style),
        //     imgSize: [radius * 2, radius * 2]
        // }),
        image: new CircleStyle({
            fill: new Fill({
                color: style?.fillColor || "#dd5e47"
            }),
            stroke: new Stroke({
                color: style?.strokeColor || "#ffffff",
                width: style?.strokeWidth || 2
            }),
            radius
        }),
        text: new Text({
            text: count.toString(),
            font: "bold " + ((style?.textSize || 14) + (isHovered ? enlargeFactor * 0.5 : 0)) + "px sans-serif",
            fill: new Fill({
                color: style?.textColor || "#ffffff"
            })
        })
    });
};

export const getSimpleSymbol = (feature: FeatureLike, styles?: IVectorLayerStyles): Style | Style[] => {
    const type = feature.getGeometry().getType();

    return type === "Polygon" || type === "MultiPolygon"
        ? getPolygonStyle(feature, styles)
        : type === "LineString" || type === "MultiLineString"
        ? getLineStyle(feature, styles)
        : type === "Point"
        ? getPointStyle(feature, styles)
        : null;
};

const getPolygonStyle = (feature: FeatureLike, styles?: IVectorLayerStyles) => {
    const isHovered = feature.get("HOVERED");

    return new Style({
        stroke: new Stroke({
            color: styles?.polygon?.strokeColor || [221, 92, 70, 1],
            width: styles?.polygon?.strokeWidth || 3
        }),
        fill: new Fill({
            color: styles?.polygon?.fillColor || [221, 92, 70, 0.5]
        }),
        text:
            styles?.polygon?.label || styles?.text
                ? new Text({
                      font: isHovered ? "bold 13px sans-serif" : styles?.text?.font || "bold 12px sans-serif",
                      fill: new Fill({
                          color: styles?.text?.fillColor || "white"
                      }),
                      stroke: new Stroke({
                          color: styles?.text?.strokeColor || "white",
                          width: styles?.text?.strokeWidth || 2
                      }),
                      text: replaceFieldsWithCalculations(feature, styles?.text?.text),
                      overflow: styles?.text?.overflow,
                      padding: styles?.text?.padding,
                      backgroundFill: styles?.text?.backgroundFill
                          ? new Fill({ color: styles?.text?.backgroundFill?.color })
                          : null
                  })
                : undefined
    });
};

const getLineStyle = (feature: FeatureLike, styles?: IVectorLayerStyles) => {
    const isHovered = feature.get("HOVERED");
    const featureStyles = [
        new Style({
            stroke: new Stroke({
                color: styles?.polyline?.strokeColor || "white",
                width: styles?.polyline?.strokeWidth || 1.5
            }),
            text: styles?.polyline?.label
                ? new Text({
                      font: isHovered ? "bold 13px sans-serif" : "bold 12px sans-serif",
                      placement: "line",
                      //   maxAngle: Math.PI / 6,
                      //   padding: [0, 50, 0, 50],
                      fill: new Fill({
                          color: "white"
                      }),
                      stroke: new Stroke({
                          color: styles?.polyline?.strokeColor || "white",
                          width: 2
                      }),
                      text: replaceFieldsWithValues(feature, styles?.polyline?.label)
                  })
                : undefined
        })
    ];
    if (isHovered) {
        featureStyles.push(
            new Style({
                stroke: new Stroke({
                    color: styles?.polyline?.highlightStyle?.strokeColor || "white",
                    width: styles?.polyline?.highlightStyle?.strokeWidth || 10
                })
            })
        );
    }
    return featureStyles;
};

const getPointStyle = (featureLike: FeatureLike, styles?: IVectorLayerStyles) => {
    const includedFeat = featureLike.get("features");
    const feature = includedFeat ? includedFeat[0] : featureLike;
    const isHovered = feature.get("HOVERED");
    let src = styles?.icon;

    const featureStyles: Style[] = [];

    if (styles?.icons) {
        const val = feature.get(styles.icons.field);
        const srcVals = styles.icons.values.find((value) => value[val]);
        src = srcVals && srcVals[val];
    }
    featureStyles.push(
        new Style({
            image: src
                ? new Icon({ src, width: 32, height: 32 })
                : new CircleStyle({
                      fill: new Fill({
                          color: styles?.point?.fillColor || "#dd5e47"
                      }),
                      stroke: new Stroke({
                          color: styles?.point?.strokeColor || "white",
                          width: styles?.point?.strokeWidth || 1.5
                      }),
                      radius: styles?.point?.radius || 7
                  })
        })
    );
    if (styles?.labels && styles?.labels.length) {
        addLabels(feature, featureStyles, styles);
    }
    if (isHovered) {
        featureStyles.push(
            new Style({
                image: new CircleStyle({
                    fill: new Fill({
                        color: styles?.point?.highlightStyle?.fillColor || "#dd5e47"
                    }),
                    stroke: new Stroke({
                        color: styles?.point?.highlightStyle?.strokeColor || "white",
                        width: styles?.point?.highlightStyle?.strokeWidth || 10
                    }),
                    radius: styles?.point?.highlightStyle?.radius || 7
                })
            })
        );
    }
    return featureStyles;
};

const addLabels = (feature: Feature, featureStyles: Style[], styles: IVectorLayerStyles) => {
    //@ts-ignore
    const mapScale = document.mapScale;
    const labelsForScale = styles.labels?.find((hl) => hl.maxScale >= mapScale && mapScale > hl.minScale);

    if (labelsForScale) {
        const labelCoords = feature.get("LABELCOORDS");

        featureStyles.push(
            ...labelsForScale.labels.map((label) => {
                const style = new Style(parseLabelConfig(label, feature));
                labelCoords?.length && style.setGeometry(new Point(labelCoords));
                style.setZIndex(9999);
                return style;
            })
        );
    }
};

const parseLabelConfig = (sourceObj, feat?: FeatureLike) => {
    if (typeof sourceObj !== "object" || sourceObj === null) {
        return sourceObj;
    }

    const newObj = Array.isArray(sourceObj) ? [] : {};
    for (const key in sourceObj) {
        if (Object.hasOwnProperty.call(sourceObj, key)) {
            key === "fill"
                ? (newObj[key] = new Fill(parseLabelConfig(sourceObj[key])))
                : key === "backgroundFill"
                ? (newObj[key] = new Fill(parseLabelConfig(sourceObj[key])))
                : key === "stroke"
                ? (newObj[key] = new Stroke(parseLabelConfig(sourceObj[key])))
                : key === "labelText"
                ? (newObj["text"] = new Text(parseLabelConfig(sourceObj[key], feat)))
                : key === "text"
                ? (newObj["text"] = replaceFieldsWithValues(feat, sourceObj[key]))
                : key === "regularShape"
                ? (newObj["image"] = new RegularShape(parseLabelConfig(sourceObj[key])))
                : (newObj[key] = parseLabelConfig(sourceObj[key]));
        }
    }

    return newObj;
};
